/**
 * Extreme Events Modeling Engine
 * Advanced Monte Carlo simulation with power law extreme events
 * Implements hybrid simulation framework with normal variables + extreme events
 */

import { logger } from '../utils/logger';
import { calculateFinancials } from '../services/financialCalculations';
import { PowerLawDistribution, ExtremeEventGenerator } from '../utils/powerLawUtils';
import { 
    RISK_CATEGORIES, 
    EXTREME_EVENTS, 
    getEnabledEvents 
} from '../constants/extremeEventsConfig';
import jStat from 'jstat';

/**
 * Hybrid Monte Carlo Simulation Engine
 * Combines normal market variability with power law extreme events
 */
export class HybridMonteCarloSimulation {
    constructor(baseInputs, config) {
        this.baseInputs = baseInputs;
        this.config = config;
        this.scenarios = config.scenarios || 1000;
        this.simulationMode = config.simulationMode || 'hybrid';
        this.results = [];
        this.extremeEventResults = [];
        this.distributions = {};
        this.correlationMatrix = {};
        
        // Initialize extreme event generators by category
        this.eventGenerators = {};
        this.initializeEventGenerators();
        
        logger.info('Hybrid Monte Carlo simulation initialized', { 
            scenarios: this.scenarios, 
            mode: this.simulationMode,
            enabledEvents: getEnabledEvents().length
        });
    }

    /**
     * Initialize extreme event generators for each risk category
     */
    initializeEventGenerators() {
        Object.keys(RISK_CATEGORIES).forEach(categoryId => {
            const category = RISK_CATEGORIES[categoryId];
            if (!category.enabled || !this.config.riskCategories?.[categoryId]?.enabled) {
                return;
            }

            const categoryEvents = EXTREME_EVENTS[categoryId]?.filter(event => 
                this.config.eventStates?.[event.id] !== false
            ).map(event => {
                // Use modified parameters if available, otherwise use defaults
                const modifiedParams = this.config.eventParameters?.[event.id];
                if (modifiedParams) {
                    return {
                        ...event,
                        probability: modifiedParams.probability,
                        impactRange: [modifiedParams.impactRangeLow, modifiedParams.impactRangeHigh]
                    };
                }
                return event;
            }) || [];

            if (categoryEvents.length > 0) {
                this.eventGenerators[categoryId] = new ExtremeEventGenerator(
                    categoryEvents,
                    category.powerLawParams
                );
                
                logger.debug(`Event generator created for ${categoryId}`, {
                    events: categoryEvents.length,
                    powerLawParams: category.powerLawParams
                });
            }
        });
    }

    /**
     * Generate normal market variations for external parameters
     * @param {Object} externalParams - External parameter distributions
     * @returns {Object} Random parameter values
     */
    generateNormalVariations(externalParams) {
        const variations = {};
        
        Object.entries(externalParams).forEach(([paramId, param]) => {
            if (param.std > 0) {
                // Generate normal random value
                let value = jStat.normal.sample(param.mean, param.std);
                
                // Apply bounds checking (ensure positive values for most parameters)
                if (paramId.includes('Rate') || paramId.includes('Cost') || paramId.includes('Price')) {
                    value = Math.max(0, value);
                }
                
                // Apply correlation adjustments if enabled
                if (this.config.correlationModeling) {
                    value = this.applyCorrelation(paramId, value, variations);
                }
                
                variations[paramId] = value;
            } else {
                variations[paramId] = param.mean;
            }
        });
        
        return variations;
    }

    /**
     * Apply correlation adjustments between related parameters
     * @param {string} paramId - Parameter ID
     * @param {number} value - Generated value
     * @param {Object} existingValues - Already generated values
     * @returns {number} Adjusted value
     */
    applyCorrelation(paramId, value, existingValues) {
        // Define correlation relationships
        const correlations = {
            longTermLoanRate: { discountRate: 0.7 }, // Interest rates are correlated
            pvDegradationRate: { operatingCostRate: 0.3 }, // Higher degradation -> higher maintenance
            bessArbitrageSpread: { onGridTariff: 0.5 }, // Arbitrage depends on grid pricing
            vatRate: { incomeTaxRate: 0.4 }, // Tax rates somewhat correlated
            landCostPerHectare: { investmentPerMW: 0.2 } // Land and investment costs loosely correlated
        };

        if (!correlations[paramId]) {
            return value;
        }

        let adjustedValue = value;
        Object.entries(correlations[paramId]).forEach(([correlatedParam, correlation]) => {
            if (existingValues[correlatedParam] !== undefined) {
                const baseParam = this.config.externalParameters[correlatedParam];
                const deviation = (existingValues[correlatedParam] - baseParam.mean) / baseParam.std;
                const adjustment = correlation * deviation * this.config.externalParameters[paramId].std;
                adjustedValue += adjustment;
            }
        });

        return adjustedValue;
    }

    /**
     * Sample extreme events for a given scenario
     * @returns {Object|null} Extreme event or null if no event occurs
     */
    sampleExtremeEvents() {
        const events = [];
        
        // Sample from each enabled category
        Object.entries(this.eventGenerators).forEach(([categoryId, generator]) => {
            const event = generator.sampleEvent();
            if (event) {
                events.push({
                    ...event,
                    categoryId
                });
            }
        });

        // If multiple events occur, handle cascading effects
        if (events.length > 1 && this.config.cascadingEffects) {
            return this.applyCascadingEffects(events);
        } else if (events.length > 0) {
            // Return the most severe event
            return events.reduce((mostSevere, current) => 
                current.impactMultiplier < mostSevere.impactMultiplier ? current : mostSevere
            );
        }

        return null;
    }

    /**
     * Apply cascading effects when multiple extreme events occur
     * @param {Array} events - Array of extreme events
     * @returns {Object} Combined event with cascading effects
     */
    applyCascadingEffects(events) {
        // Define cascading relationships
        const cascades = {
            major_earthquake: ['tsunami', 'utility_grid_instability', 'access_route_destruction'],
            super_typhoon: ['extended_flooding', 'utility_grid_instability', 'supply_chain_collapse'],
            volcanic_eruption: ['extended_flooding', 'access_route_destruction', 'supply_chain_collapse'],
            geopolitical_conflict: ['supply_chain_collapse', 'currency_inconvertibility', 'foreign_market_protectionism'],
            complete_grid_collapse: ['utility_grid_instability', 'cyberattack_grid_systems', 'major_equipment_failure']
        };

        // Calculate compound impact
        let compoundImpact = 1.0;
        const triggeredEvents = [];
        
        events.forEach(event => {
            // Apply primary event impact
            compoundImpact *= event.impactMultiplier;
            triggeredEvents.push(event);
            
            // Check for cascading events
            const cascadingEvents = cascades[event.event.id] || [];
            cascadingEvents.forEach(cascadeId => {
                // Add secondary impact (typically 20-50% of primary)
                const secondaryImpact = 0.3 * (1 - event.impactMultiplier);
                compoundImpact *= (1 - secondaryImpact);
                
                triggeredEvents.push({
                    event: { id: cascadeId, name: `Cascading: ${cascadeId}` },
                    impactMultiplier: 1 - secondaryImpact,
                    cascadeSource: event.event.id,
                    categoryId: 'CASCADE'
                });
            });
        });

        // Ensure impact doesn't exceed total loss
        compoundImpact = Math.max(0, compoundImpact);

        return {
            event: { 
                id: 'compound_extreme_event',
                name: `Compound Event (${events.length} triggers)`,
                isCascading: true
            },
            impactMultiplier: compoundImpact,
            primaryEvents: events,
            triggeredEvents,
            categoryId: 'COMPOUND'
        };
    }

    /**
     * Run the hybrid Monte Carlo simulation
     * @returns {Object} Simulation results
     */
    async runSimulation() {
        logger.info('Starting hybrid Monte Carlo simulation', { 
            scenarios: this.scenarios,
            mode: this.simulationMode
        });

        this.results = [];
        this.extremeEventResults = [];

        for (let i = 0; i < this.scenarios; i++) {
            try {
                // Start with base inputs
                let scenarioInputs = { ...this.baseInputs };
                let extremeEventImpact = null;

                // Apply normal market variations (unless extreme_only mode)
                if (this.simulationMode !== 'extreme_only') {
                    const normalVariations = this.generateNormalVariations(this.config.externalParameters);
                    scenarioInputs = { ...scenarioInputs, ...normalVariations };
                }

                // Apply extreme events (unless normal_only mode)
                if (this.simulationMode !== 'normal_only') {
                    extremeEventImpact = this.sampleExtremeEvents();
                }

                // Calculate base financial results
                let financialResults = calculateFinancials(scenarioInputs);

                // Apply extreme event impacts to financial results
                if (extremeEventImpact) {
                    financialResults = this.applyExtremeEventImpact(
                        financialResults, 
                        extremeEventImpact
                    );
                    
                    this.extremeEventResults.push({
                        scenario: i + 1,
                        extremeEvent: extremeEventImpact,
                        originalResults: calculateFinancials(scenarioInputs),
                        impactedResults: financialResults
                    });
                }

                // Store scenario results - safely extract metrics from indicators
                const indicators = financialResults.indicators || {};
                this.results.push({
                    scenario: i + 1,
                    inputs: scenarioInputs,
                    projectIRR: indicators.projectIRR || 0,
                    capitalIRR: indicators.capitalIRR || 0,
                    projectNPV: indicators.projectNPV || 0,
                    capitalNPV: indicators.capitalNPV || 0,
                    lcoe: indicators.lcoe || 0,
                    totalBessRevenue: financialResults.bessStatement?.reduce((total, year) => 
                        total + (year.arbitrageRevenue || 0) + (year.ancillaryRevenue || 0) + 
                               (year.capacityRevenue || 0) + (year.demandChargeReduction || 0), 0) || 0,
                    projectPaybackPeriod: indicators.paybackPeriod || 25,
                    capitalPaybackPeriod: indicators.discountedPaybackPeriod || 25,
                    extremeEvent: extremeEventImpact,
                    hasExtremeEvent: extremeEventImpact !== null
                });

                // Progress logging
                if (i % 100 === 0) {
                    logger.debug(`Hybrid Monte Carlo progress: ${i}/${this.scenarios} scenarios`);
                }

            } catch (error) {
                logger.warn(`Scenario ${i + 1} failed`, { error: error.message });
                continue;
            }
        }

        // Calculate statistical distributions
        this.calculateDistributions();
        
        // Calculate extreme event statistics
        const extremeEventStats = this.calculateExtremeEventStatistics();

        logger.info('Hybrid Monte Carlo simulation completed', {
            successfulScenarios: this.results.length,
            extremeEvents: this.extremeEventResults.length,
            extremeEventRate: this.extremeEventResults.length / this.results.length
        });

        return {
            scenarios: this.results,
            distributions: this.distributions,
            extremeEventResults: this.extremeEventResults,
            extremeEventStatistics: extremeEventStats,
            summary: {
                totalScenarios: this.scenarios,
                successfulScenarios: this.results.length,
                extremeEventScenarios: this.extremeEventResults.length,
                extremeEventRate: this.extremeEventResults.length / this.results.length,
                simulationMode: this.simulationMode,
                failureRate: 1 - (this.results.length / this.scenarios)
            }
        };
    }

    /**
     * Apply extreme event impact to financial results
     * @param {Object} baseResults - Base financial calculation results
     * @param {Object} extremeEvent - Extreme event data
     * @returns {Object} Modified financial results
     */
    applyExtremeEventImpact(baseResults, extremeEvent) {
        const impactMultiplier = extremeEvent.impactMultiplier;
        
        // Apply impact to key financial metrics
        const impactedResults = { ...baseResults };
        
        // Ensure indicators object exists
        if (!impactedResults.indicators) {
            impactedResults.indicators = {};
        }
        
        // Direct impact on NPV and cash flows
        if (impactedResults.indicators.projectNPV !== null && impactedResults.indicators.projectNPV !== undefined) {
            impactedResults.indicators.projectNPV *= impactMultiplier;
        }
        if (impactedResults.indicators.capitalNPV !== null && impactedResults.indicators.capitalNPV !== undefined) {
            impactedResults.indicators.capitalNPV *= impactMultiplier;
        }

        // Impact on revenues affects IRR calculation
        if (impactedResults.totalRevenue) {
            impactedResults.totalRevenue *= impactMultiplier;
        }
        if (impactedResults.totalBessRevenue) {
            impactedResults.totalBessRevenue *= impactMultiplier;
        }

        // Recalculate IRR with impacted cash flows
        try {
            // Simplified IRR adjustment (in practice, would recalculate full cash flows)
            const baseProjectIRR = baseResults.indicators?.projectIRR || 0;
            const baseCapitalIRR = baseResults.indicators?.capitalIRR || 0;
            
            if (impactMultiplier < 0.5) {
                // Severe impact - IRR becomes negative or very low
                impactedResults.indicators.projectIRR = Math.max(-0.5, baseProjectIRR * impactMultiplier);
                impactedResults.indicators.capitalIRR = Math.max(-0.5, baseCapitalIRR * impactMultiplier);
            } else {
                // Moderate impact - proportional IRR reduction
                impactedResults.indicators.projectIRR = baseProjectIRR * (0.5 + 0.5 * impactMultiplier);
                impactedResults.indicators.capitalIRR = baseCapitalIRR * (0.5 + 0.5 * impactMultiplier);
            }
        } catch (error) {
            logger.warn('IRR recalculation failed after extreme event', { error: error.message });
        }

        // LCOE adjustment (inverse relationship - higher LCOE with lower generation)
        if (impactedResults.indicators.lcoe && impactMultiplier < 1.0) {
            impactedResults.indicators.lcoe = (baseResults.indicators?.lcoe || 0) / impactMultiplier;
        }

        // Payback period adjustment
        if (impactMultiplier < 1.0) {
            impactedResults.indicators.paybackPeriod = (baseResults.indicators?.paybackPeriod || 25) / impactMultiplier;
            impactedResults.indicators.discountedPaybackPeriod = (baseResults.indicators?.discountedPaybackPeriod || 25) / impactMultiplier;
        }

        return impactedResults;
    }

    /**
     * Calculate statistical distributions for all metrics
     */
    calculateDistributions() {
        const metrics = ['projectIRR', 'capitalIRR', 'projectNPV', 'capitalNPV', 'lcoe'];
        
        metrics.forEach(metric => {
            const values = this.results.map(r => r[metric]).filter(v => v !== null && isFinite(v));
            
            if (values.length > 0) {
                const sortedValues = values.sort((a, b) => a - b);
                
                this.distributions[metric] = {
                    mean: values.reduce((sum, val) => sum + val, 0) / values.length,
                    median: sortedValues[Math.floor(sortedValues.length / 2)],
                    std: Math.sqrt(values.reduce((sum, val) => {
                        const mean = this.distributions[metric]?.mean || 0;
                        return sum + Math.pow(val - mean, 2);
                    }, 0) / values.length),
                    min: Math.min(...values),
                    max: Math.max(...values),
                    p5: sortedValues[Math.floor(sortedValues.length * 0.05)],
                    p25: sortedValues[Math.floor(sortedValues.length * 0.25)],
                    p75: sortedValues[Math.floor(sortedValues.length * 0.75)],
                    p95: sortedValues[Math.floor(sortedValues.length * 0.95)],
                    count: values.length
                };
            }
        });

        // Calculate distributions for extreme event scenarios only
        const extremeEventValues = this.results
            .filter(r => r.hasExtremeEvent)
            .map(r => ({
                projectIRR: r.projectIRR,
                capitalIRR: r.capitalIRR,
                projectNPV: r.projectNPV,
                capitalNPV: r.capitalNPV
            }));

        if (extremeEventValues.length > 0) {
            this.distributions.extremeEventOnly = {};
            metrics.forEach(metric => {
                const values = extremeEventValues.map(r => r[metric]).filter(v => v !== null && isFinite(v));
                if (values.length > 0) {
                    const sortedValues = values.sort((a, b) => a - b);
                    this.distributions.extremeEventOnly[metric] = {
                        mean: values.reduce((sum, val) => sum + val, 0) / values.length,
                        median: sortedValues[Math.floor(sortedValues.length / 2)],
                        min: Math.min(...values),
                        max: Math.max(...values),
                        count: values.length
                    };
                }
            });
        }
    }

    /**
     * Calculate extreme event statistics and analysis
     * @returns {Object} Extreme event statistics
     */
    calculateExtremeEventStatistics() {
        const eventsByCategory = {};
        const eventsBySeverity = {};
        const cascadingEvents = [];
        
        this.extremeEventResults.forEach(result => {
            const event = result.extremeEvent;
            const categoryId = event.categoryId;
            
            // Count by category
            if (!eventsByCategory[categoryId]) {
                eventsByCategory[categoryId] = {
                    count: 0,
                    totalImpact: 0,
                    averageImpact: 0,
                    events: []
                };
            }
            eventsByCategory[categoryId].count++;
            eventsByCategory[categoryId].totalImpact += (1 - event.impactMultiplier);
            eventsByCategory[categoryId].events.push(event);
            
            // Count by severity
            const severity = event.event.severity || 'unknown';
            if (!eventsBySeverity[severity]) {
                eventsBySeverity[severity] = { count: 0, totalImpact: 0 };
            }
            eventsBySeverity[severity].count++;
            eventsBySeverity[severity].totalImpact += (1 - event.impactMultiplier);
            
            // Track cascading events
            if (event.event.isCascading) {
                cascadingEvents.push(event);
            }
        });

        // Calculate averages
        Object.keys(eventsByCategory).forEach(categoryId => {
            const category = eventsByCategory[categoryId];
            category.averageImpact = category.totalImpact / category.count;
        });

        // Calculate Value at Risk (VaR) for extreme events
        const extremeEventNPVs = this.extremeEventResults.map(r => r.impactedResults.projectNPV);
        const var95 = this.calculateValueAtRisk(extremeEventNPVs, 0.95);
        const var99 = this.calculateValueAtRisk(extremeEventNPVs, 0.99);

        return {
            totalExtremeEvents: this.extremeEventResults.length,
            extremeEventRate: this.extremeEventResults.length / this.results.length,
            eventsByCategory,
            eventsBySeverity,
            cascadingEvents: cascadingEvents.length,
            cascadingRate: cascadingEvents.length / this.extremeEventResults.length,
            valueAtRisk: {
                var95,
                var99,
                expectedShortfall95: this.calculateExpectedShortfall(extremeEventNPVs, 0.95),
                expectedShortfall99: this.calculateExpectedShortfall(extremeEventNPVs, 0.99)
            },
            worstCaseScenario: extremeEventNPVs.length > 0 ? Math.min(...extremeEventNPVs) : null,
            averageExtremeEventImpact: this.extremeEventResults.length > 0 
                ? this.extremeEventResults.reduce((sum, r) => sum + (1 - r.extremeEvent.impactMultiplier), 0) / this.extremeEventResults.length 
                : 0
        };
    }

    /**
     * Calculate Value at Risk for a given confidence level
     * @param {Array} values - Array of values
     * @param {number} confidenceLevel - Confidence level (e.g., 0.95)
     * @returns {number} VaR value
     */
    calculateValueAtRisk(values, confidenceLevel) {
        if (values.length === 0) return null;
        
        const sortedValues = values.sort((a, b) => a - b);
        const index = Math.floor(sortedValues.length * (1 - confidenceLevel));
        return sortedValues[index];
    }

    /**
     * Calculate Expected Shortfall (Conditional VaR)
     * @param {Array} values - Array of values
     * @param {number} confidenceLevel - Confidence level (e.g., 0.95)
     * @returns {number} Expected shortfall
     */
    calculateExpectedShortfall(values, confidenceLevel) {
        if (values.length === 0) return null;
        
        const var_ = this.calculateValueAtRisk(values, confidenceLevel);
        const tailValues = values.filter(v => v <= var_);
        
        if (tailValues.length === 0) return var_;
        
        return tailValues.reduce((sum, val) => sum + val, 0) / tailValues.length;
    }

    /**
     * Export simulation results
     * @returns {Object} Export data
     */
    exportResults() {
        return {
            summary: {
                totalScenarios: this.scenarios,
                successfulScenarios: this.results.length,
                extremeEventScenarios: this.extremeEventResults.length,
                simulationMode: this.simulationMode,
                timestamp: new Date().toISOString()
            },
            distributions: this.distributions,
            extremeEventStatistics: this.calculateExtremeEventStatistics(),
            configuration: this.config,
            scenarios: this.results.slice(0, 100), // Export first 100 scenarios
            extremeEventScenarios: this.extremeEventResults
        };
    }

    /**
     * Get risk metrics including extreme event impacts
     * @returns {Object} Risk metrics
     */
    getRiskMetrics() {
        const baseMetrics = this.getBaseRiskMetrics();
        const extremeEventMetrics = this.getExtremeEventRiskMetrics();
        
        return {
            ...baseMetrics,
            extremeEvents: extremeEventMetrics,
            tailRisk: {
                probabilityOfCatastrophicLoss: this.results.filter(r => r.projectNPV < 0.1 * this.baseInputs.ownFunds).length / this.results.length,
                probabilityOfTotalLoss: this.results.filter(r => r.projectNPV <= 0).length / this.results.length,
                maxDrawdown: Math.min(...this.results.map(r => r.projectNPV)),
                extremeEventContribution: this.extremeEventResults.length / this.results.length
            }
        };
    }

    /**
     * Get base risk metrics (without extreme events)
     * @returns {Object} Base risk metrics
     */
    getBaseRiskMetrics() {
        const normalScenarios = this.results.filter(r => !r.hasExtremeEvent);
        
        if (normalScenarios.length === 0) {
            return { probabilityOfLoss: 0, probabilityOfLowReturns: 0 };
        }

        const projectIRRValues = normalScenarios.map(r => r.projectIRR).filter(v => v !== null && isFinite(v));
        const projectNPVValues = normalScenarios.map(r => r.projectNPV).filter(v => v !== null && isFinite(v));

        return {
            probabilityOfLoss: projectNPVValues.filter(v => v < 0).length / projectNPVValues.length,
            probabilityOfLowReturns: projectIRRValues.filter(v => v < 0.05).length / projectIRRValues.length,
            baseScenarios: normalScenarios.length
        };
    }

    /**
     * Get extreme event specific risk metrics
     * @returns {Object} Extreme event risk metrics
     */
    getExtremeEventRiskMetrics() {
        if (this.extremeEventResults.length === 0) {
            return { 
                probabilityOfExtremeEvent: 0,
                averageExtremeEventImpact: 0,
                maxExtremeEventImpact: 0
            };
        }

        const impacts = this.extremeEventResults.map(r => 1 - r.extremeEvent.impactMultiplier);
        
        return {
            probabilityOfExtremeEvent: this.extremeEventResults.length / this.results.length,
            averageExtremeEventImpact: impacts.reduce((sum, val) => sum + val, 0) / impacts.length,
            maxExtremeEventImpact: Math.max(...impacts),
            catastrophicEventRate: this.extremeEventResults.filter(r => r.extremeEvent.impactMultiplier < 0.3).length / this.results.length,
            cascadingEventRate: this.extremeEventResults.filter(r => r.extremeEvent.event.isCascading).length / this.results.length
        };
    }
}

export default {
    HybridMonteCarloSimulation
};